import { BehaviorSubject, Observable } from 'rxjs';
import { Validateable } from '@app/calculators/effort-planner-form/form-validation/validateable';
import { AccountDTO, AccountModel, AccountModelData } from './accountModel';
import { OrganisationDTO, OrganisationModel, OrganisationModelData } from './organisationModel';

export interface RegistrationDTO {
  account: AccountDTO;
  organisation: OrganisationDTO;
  numAdditionalUsers: number;
  token: string;
  message?: string;
}

export class RegistrationModelData {
  numAdditionalUsers: number | undefined;
  account: AccountModelData | undefined;
  organisation: OrganisationModelData | undefined;
  token: string | undefined;
  message?: string;
}

export class RegistrationModel implements Validateable {
  private numAdditionalUsersSubject$: BehaviorSubject<number>;
  private billingAddressSameAsOrganisationSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private messageSubject$: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>(undefined);
  readonly numAdditionalUsers$: Observable<number>;
  readonly account: AccountModel;
  readonly organisation: OrganisationModel;

  readonly billingAddressSameAsOrganisation$: Observable<boolean> =
    this.billingAddressSameAsOrganisationSubject$.asObservable();

  readonly message$: Observable<string | undefined>;

  constructor(data: RegistrationModelData | undefined) {
    this.numAdditionalUsersSubject$ = new BehaviorSubject<number>(data?.numAdditionalUsers ?? 0);

    this.numAdditionalUsers$ = this.numAdditionalUsersSubject$.asObservable();
    this.account = new AccountModel(data?.account);
    this.organisation = new OrganisationModel(data?.organisation, undefined, this);
    this.message$ = this.messageSubject$.asObservable();
  }

  get numAdditionalUsers() {
    return this.numAdditionalUsersSubject$.value;
  }

  set numAdditionalUsers(value: number) {
    this.numAdditionalUsersSubject$.next(value);
  }

  get billingAddressSameAsOrganisation() {
    return this.billingAddressSameAsOrganisationSubject$.value;
  }

  set billingAddressSameAsOrganisation(value: boolean) {
    this.billingAddressSameAsOrganisationSubject$.next(value);
  }

  get message() {
    return this.messageSubject$.value;
  }

  set message(value: string | undefined) {
    this.messageSubject$.next(value);
  }

  resetValidations(): void {
    this.account.resetValidations();
    this.organisation.resetValidations();
  }

  validate(): void {
    this.account.validate();
    this.organisation.validate();
  }

  isValid(): boolean {
    return this.account.isValid() && this.organisation.isValid();
  }

  toDto(): RegistrationDTO {
    const accountDto = this.account.toDto();
    const organisationDto = this.organisation.toDto();
    return {
      account: accountDto,
      organisation: organisationDto,
      numAdditionalUsers: this.numAdditionalUsers,
      token: '',
      message: this.message,
    };
  }
}
